import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Shield, ShoppingBag, Diamond, Gift } from "lucide-react";
import { useState, useEffect } from "react";
import logoIcon from "@/assets/logo-icon.png";
import LogoIcon from "@/components/LogoIcon";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from "@/components/NotificationBell";
import { useToast } from "@/hooks/use-toast";
import { NavLink } from "@/components/NavLink";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isSeller, setIsSeller] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkUser();
    });
    return () => subscription.unsubscribe();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        if (userError.message !== "Auth session missing!") {
          console.error("Error fetching user:", userError);
        }
        setUser(null);
        setIsSeller(false);
        setIsAdmin(false);
        return;
      }

      setUser(user);
      
      if (user) {
        const [profileResponse, roleResponse] = await Promise.all([
          supabase.from("profiles").select("user_type").eq("user_id", user.id).single(),
          supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle()
        ]);

        if (profileResponse.error) {
          console.error("Error fetching profile:", profileResponse.error);
        }
        if (roleResponse.error) {
          console.error("Error fetching role:", roleResponse.error);
        }

        setIsSeller(profileResponse.data?.user_type === "seller");
        setIsAdmin(!!roleResponse.data);
      } else {
        setIsSeller(false);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("Error in checkUser:", error);
      setUser(null);
      setIsSeller(false);
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleUploadArtwork = () => {
    if (!isSeller) {
      toast({
        title: "Seller Account Required",
        description: "You need to become a Seller to upload artwork",
        variant: "destructive",
      });
      return;
    }
    navigate("/dashboard");
  };

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/gallery", label: "Gallery" },
    { to: "/marketplace", label: "Marketplace" },
    { to: "/favorites", label: "Favorites" },
    { to: "/art-analysis", label: "Analysis" },
    { to: "/search", label: "Search" },
    { to: "/art-auction", label: "Auctions" },
    { to: "/art-news", label: "News" },
    { to: "/forum", label: "Forum" },
    { to: "/network", label: "Network" },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-background/98 backdrop-blur-xl shadow-soft border-b border-border/50' 
          : 'bg-transparent'
      }`}>
        {/* Luxury gold top accent line */}
        <div className="h-px w-full bg-gradient-gold opacity-60" />
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <LogoIcon className="w-9 h-9 group-hover:scale-105 transition-all duration-500 drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]" />
              <span className="text-xl font-display font-semibold luxury-text tracking-wide">
                Art Experto
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 flex-wrap justify-center flex-1 px-8">
              {navLinks.map((link) => (
                <NavLink key={link.to} to={link.to}>
                  {link.label}
                </NavLink>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              {user && <NotificationBell />}
              {user && (
                <Button 
                  onClick={handleUploadArtwork} 
                  variant="ghost" 
                  size="sm"
                  className="text-xs tracking-wide hover:text-primary"
                >
                  Upload
                </Button>
              )}
              {user && !isSeller && (
                <Button 
                  onClick={() => navigate("/buyer-dashboard")} 
                  variant="ghost" 
                  size="sm"
                  className="text-xs tracking-wide"
                >
                  <ShoppingBag className="h-4 w-4 mr-1" />
                  Dashboard
                </Button>
              )}
              {isSeller && (
                <Button 
                  onClick={() => navigate("/my-artworks")} 
                  variant="ghost" 
                  size="sm"
                  className="text-xs tracking-wide"
                >
                  My Artworks
                </Button>
              )}
              {user && (
                <Button 
                  onClick={() => navigate("/referrals")} 
                  variant="ghost" 
                  size="sm"
                  className="text-xs tracking-wide"
                >
                  <Gift className="h-4 w-4 mr-1" />
                  Referrals
                </Button>
              )}
              {isAdmin && (
                <Button 
                  onClick={() => navigate("/admin")} 
                  variant="ghost" 
                  size="sm"
                  className="text-primary text-xs tracking-wide"
                >
                  <Shield className="h-4 w-4 mr-1" />
                  Admin
                </Button>
              )}
              {user ? (
                <Button 
                  onClick={handleLogout} 
                  variant="outline" 
                  size="sm"
                  className="text-xs tracking-wide luxury-border"
                >
                  Sign out
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate("/auth")} 
                  size="sm"
                  className="bg-gradient-hero text-primary-foreground shadow-gold hover:shadow-glow text-xs tracking-wide transition-all duration-500"
                >
                  <Diamond className="h-3 w-3 mr-1" />
                  Sign in
                </Button>
              )}
            </div>

            {/* Mobile right side: notification + hamburger */}
            <div className="lg:hidden flex items-center gap-2">
              {user && <NotificationBell />}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                className="p-2 rounded-lg text-foreground hover:text-primary hover:bg-primary/8 transition-all duration-300 z-50 relative"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="lg:hidden fixed inset-0 z-40 bg-background/60 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Menu Panel */}
          <div className="lg:hidden fixed top-[calc(1px+4rem)] left-0 right-0 z-40 bg-background/98 backdrop-blur-xl border-b border-border/50 shadow-strong animate-fade-in max-h-[calc(100vh-5rem)] overflow-y-auto">
            {/* Gold accent line at top of menu */}
            <div className="h-px w-full bg-gradient-gold opacity-40" />

            <div className="container mx-auto px-4 py-6 space-y-1">
              {/* Nav Links Grid */}
              <div className="grid grid-cols-2 gap-1 mb-4">
                {navLinks.map((link) => (
                  <Link 
                    key={link.to}
                    to={link.to} 
                    className="flex items-center gap-2 py-3 px-4 text-sm text-foreground/80 hover:text-primary hover:bg-primary/6 active:bg-primary/10 transition-all duration-200 rounded-xl font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link 
                  to="/profile" 
                  className="flex items-center gap-2 py-3 px-4 text-sm text-foreground/80 hover:text-primary hover:bg-primary/6 active:bg-primary/10 transition-all duration-200 rounded-xl font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 py-2">
                <div className="flex-1 h-px bg-border/50" />
                <Diamond className="h-2.5 w-2.5 text-primary/40" />
                <div className="flex-1 h-px bg-border/50" />
              </div>

              {/* Account Actions */}
              <div className="pt-2 space-y-2 pb-2">
                {user ? (
                  <>
                    {isSeller && (
                      <Button 
                        onClick={() => { handleUploadArtwork(); setIsMobileMenuOpen(false); }} 
                        variant="outline" 
                        className="w-full luxury-border justify-start gap-2"
                      >
                        Upload Artwork
                      </Button>
                    )}
                    {!isSeller && (
                      <Button 
                        onClick={() => { navigate("/buyer-dashboard"); setIsMobileMenuOpen(false); }} 
                        variant="outline" 
                        className="w-full luxury-border justify-start gap-2"
                      >
                        <ShoppingBag className="h-4 w-4" />
                        My Dashboard
                      </Button>
                    )}
                    {isSeller && (
                      <Button 
                        onClick={() => { navigate("/my-artworks"); setIsMobileMenuOpen(false); }} 
                        variant="outline" 
                        className="w-full luxury-border justify-start gap-2"
                      >
                        My Artworks
                      </Button>
                    )}
                    <Button 
                      onClick={() => { navigate("/referrals"); setIsMobileMenuOpen(false); }} 
                      variant="outline" 
                      className="w-full luxury-border justify-start gap-2"
                    >
                      <Gift className="h-4 w-4" />
                      Referral Program
                    </Button>
                    {isAdmin && (
                      <Button 
                        onClick={() => { navigate("/admin"); setIsMobileMenuOpen(false); }} 
                        className="w-full bg-gradient-hero text-primary-foreground justify-start gap-2"
                      >
                        <Shield className="h-4 w-4" />
                        Admin Panel
                      </Button>
                    )}
                    <Button 
                      onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} 
                      variant="outline" 
                      className="w-full justify-start gap-2"
                    >
                      Sign out
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={() => { navigate("/auth"); setIsMobileMenuOpen(false); }} 
                    className="w-full bg-gradient-hero text-primary-foreground shadow-gold py-6 text-base"
                  >
                    <Diamond className="h-4 w-4 mr-2" />
                    Sign in to Art Experto
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
export default Navbar;